import { nextTick } from "vue";
import { useImgUrl, useThemeImgUrl } from "@/hooks/useImage";
import DOMPurify from "dompurify";
import { HashOnly } from "./hash";
import { hasHtmlTag } from "./string";
import { showImagePreview, showToast } from "vant";
// import { errorReportUpload } from "@/api";

export * from "./uuid";

// export const allowedUriRegex = /^(?:(?:https?):\/\/|www\.)[\w.-]+(?:\.[\w.-]+)+[/#?]?.*$/i;

// 预编译正则表达式
const REGEX_IMG_BACKUP = /<img src="([^"]+)" alt="Screenshot">/gi;

/**
 * 特殊字符转换
 * @param str
 */
const entityMap: {
	escape: { [k: string]: string };
	unescape: { [k: string]: string };
} = {
	escape: {
		"&": "&amp;",
		"<": "&lt;",
		">": "&gt;",
		'"': "&quot;",
		"'": "&apos;",
	},
	unescape: {
		"&amp;": "&",
		"&apos;": "'",
		"&gt;": ">",
		"&lt;": "<",
		"&quot;": '"',
	},
};

const entityReg = {
	escape: RegExp("[" + Object.keys(entityMap.escape).join("") + "]", "g"),
	unescape: RegExp("(" + Object.keys(entityMap.unescape).join("|") + ")", "g"),
};

// 将HTML转义为实体
export function escapeHTMLToEntity(html: string | number) {
	if (typeof html !== "string") return html?.toString();
	return html.replace(entityReg.escape, (match) => {
		return entityMap.escape[match];
	});
}

// 将实体转回为HTML
export function unescapeEntityToHTML(str: string | number) {
	if (typeof str !== "string") return str.toString();
	return str.replace(entityReg.unescape, (match) => {
		return entityMap.unescape[match];
	});
}

//判断是否为手机端
export const isMobile = () => {
	let flag = true;
	if (/(iPhone|iPad|iPod|iOS)/i.test(navigator.userAgent)) {
		flag = true;
	} else if (/(Android)/i.test(navigator.userAgent)) {
		flag = true;
	} else if (window.innerWidth < 600) {
		flag = true;
	} else {
		flag = false;
	}
	return flag;
};
export const getUrlParams = (key: string): string | null => {
	const href = location.href;
	const index = href.indexOf("?");
	let val: string | null = null;
	if (index !== -1) {
		const str = href.substring(index + 1);
		const arr = str.split("&");
		for (let i = 0; i < arr.length; i++) {
			const arrItem = arr[i].split("=");
			// setCookie(arrItem[0], arrItem[1])
			if (arrItem[0] === key) {
				val = arrItem[1];
				break;
			}
		}
	}
	return val;
};

/**
 * 输入框过滤标签和特殊处理
 * @param content
 */
export const dealContent = (content: string) => {
	content = content.replace(REGEX_IMG_BACKUP, (_match, $1) => `[app-img-src=${$1}]`);
	content = dealTextCon(content);
	content = content.replace(/\[app-img-src=(.*?)\]/g, (_match, $1) => {
		return `<img src=${$1} class="max-w-[144px] max-h-40 inline-block" alt="Screenshot" />`;
	});
	return unescapeEntityToHTML(content);
};

/**
 * 将字符串中的裸露 URL（http(s):// 或 www.开头的）转换为 <a> 标签，
 * 同时保留已有的 <a> 标签内容不被替换。
 *
 * @param input - 原始包含文本或 HTML 的字符串
 * @returns 替换后的字符串，带有安全的 <a> 标签链接
 */
function replaceLinksToAnchor(input: string): string {
	// 正则匹配 http(s):// 和 www. 开头的 URL
	const urlRegex = /(https?:\/\/[^\s<>"'(),;]+)|(www\.[^\s<>"'(),;]+)/gi;

	// 匹配已有的 <a> 标签，避免重复处理
	const anchorRegex = /<a\s+[^>]*>.*?<\/a>/gi;

	// 保存已有的 <a> 标签内容
	const anchors: string[] = [];
	let anchorIndex = 0;

	// 用占位符替换已有的 <a> 标签，防止后续 URL 替换时干扰已有链接
	let processedText = input.replace(anchorRegex, (match) => {
		anchors.push(match); // 存储原始 <a> 标签
		return `{{ANCHOR_${anchorIndex++}}}`; // 替换成占位符
	});

	// 将裸 URL 替换为 <a> 标签
	processedText = processedText.replace(urlRegex, (match, httpGroup, wwwGroup, offset) => {
		let href: string;
		let displayText: string;

		if (httpGroup) {
			// 匹配到了 http(s):// 的 URL
			href = httpGroup;
			// 去除前缀 http(s):// 和 www. 以便更简洁地展示文本
			displayText = httpGroup.replace(/^https?:\/\/(www\.)?/i, "").trim();
		} else if (wwwGroup) {
			// 匹配到了 www. 开头的 URL
			href = `https://${wwwGroup}`; // 自动补全协议
			displayText = wwwGroup.replace(/^(www\.)?/i, "").trim();
		} else {
			// 万一两个都没有匹配到（理论上不会），就原样返回
			return match;
		}

		// 检查链接前一个字符是否为等号，避免 HTML 属性中的值被错误替换
		const prefixChar = processedText[offset - 1];
		if (prefixChar === "=") {
			return match; // 跳过不替换
		}

		// 构造新的 <a> 标签，带有样式和 target="_blank"
		return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-blue-500 cursor-pointer inline-block p-2">${displayText}</a>`;
	});

	// 将之前替换掉的 <a> 标签占位符恢复为原始内容
	for (let i = 0; i < anchors.length; i++) {
		processedText = processedText.replace(`{{ANCHOR_${i}}}`, anchors[i]);
	}

	// 返回最终结果
	return processedText;
}

/**
 * 输入框过滤标签和特殊处理
 * @param content
 * @param isDeSpace
 */
export const dealTextCon = (str: string | number, isWorkerMessage = false) => {
	str = str ?? "";
	if (typeof str !== "string") {
		return str.toString();
	}

	const ALLOWED_TAGS = ["br"];
	const ALLOWED_ATTR = [];
	if (isWorkerMessage) {
		ALLOWED_TAGS.push("a", "img");
		ALLOWED_ATTR.push("href", "target", "style", "rel", "class", "src", "alt");
	}
	str = DOMPurify.sanitize(str, {
		ALLOWED_TAGS,
		ALLOWED_ATTR,
		// ALLOWED_URI_REGEXP: allowedUriRegex,
	});
	// str = str
	// 	// .replace(/href/gi, " ")
	// 	.replace(/atob/gi, " ")
	// 	.replace(/passthru/gi, " ")
	// 	.replace(/exec/gi, " ")
	// 	.replace(/chroot/gi, " ")
	// 	.replace(/system/gi, " ")
	// 	.replace(/shell/gi, " ")
	// 	.replace(/shell_exec/gi, " ")
	// 	.replace(/chown/gi, " ")
	// 	.replace(/proc_open/gi, " ")
	// 	.replace(/proc_get_status/gi, " ")
	// 	.replace(/popen/gi, " ")
	// 	.replace(/ini_alter/gi, " ")
	// 	.replace(/ini_restore/gi, " ")
	// 	.replace(/openlog/gi, " ")
	// 	.replace(/syslog/gi, " ")
	// 	.replace(/readlink/gi, " ")
	// 	.replace(/symlink/gi, " ")
	// 	.replace(/popepassthru/gi, " ")
	// 	.replace(/eval/gi, " ")
	// 	.replace(/script/gi, " ")
	// 	.replace(/javascript/gi, " ")
	// 	.replace(/onload/gi, " ")
	// 	.replace(/onerror/gi, " ")
	// 	.replace(/electron/gi, " ")
	// 	.replace(/process/gi, " ");
	str = escapeHTMLToEntity(str);
	const htmlStr = unescapeEntityToHTML(unescapeEntityToHTML(str));
	const flag = hasHtmlTag(htmlStr);
	if (isWorkerMessage && flag) {
		return htmlStr;
	}
	if (isWorkerMessage) {
		str = replaceLinksToAnchor(str);
	}
	return str;
};
/**
 * 过滤所有换行
 * @param text
 */
export const filterImage = (text: string) => {
	if (!text) return text;
	const regex = /\n|\r|<br\s*\/?>/gi;
	return text?.replace(regex, "");
};

// 将带有html标签的表情包替换为它原有的字符串格式
export const emojiToStr = (content: string) => {
	content = content.replace(/<img\s+class="w-6\s+h-6\s+inline-block\s+mx-0\.5"\s+src="[^"]+"\s+alt="\[emoticon_(\d+)\]"\s*\/?>/g, "[emoticon_$1]");
	content = content.replace(
		/<img\s+alt="\[emoticon_(\d+)\]"\s+src="\/image\/default\/emoji\/emoticon_\d+\.png"\s+class="w-6\s+h-6\s+inline-block\s+mx-0\.5">/g,
		"[emoticon_$1]"
	);
	return content;
};

/**
 * 文字换行
 * @param str
 */
export const viewTextNewline = (str: any) => {
	return str.replace(/\r?\n/g, "<br/>");
};

// 将html的br换行标签替换为js中的换行符
export const replaceBrWithNewline = (html: string): string => {
	return html.replace(/<br\s*\/?>/gi, "\n");
};

/**
 * 显示文本消息处理（可能包含emoji字符）
 * @param str
 */
export const viewTextMsg = (str: string, replace = true, isWorkerMessage = false) => {
	if (typeof str === "string") {
		if (replace) {
			str = filterImage(str);
		}
		str = dealTextCon(str, isWorkerMessage);
		str = emojiToStr(str);
		str = str.replace(
			/\[emoticon_(.*?)\]/gi,
			`<img class="w-6 h-6 inline-block mx-0.5" src="${useThemeImgUrl("emoji/emoticon_$1.png")}" alt="[emoticon_$1]" />`
		);
		str = unescapeEntityToHTML(str);
		str = unescapeEntityToHTML(str);
	}
	return str;
};

export const getSelection = (sendInput: any) => {
	nextTick(() => {
		const getSelection = window.getSelection || document.getSelection;
		const range = getSelection() as any; // 创建range
		range.selectAllChildren(sendInput.value); // range 选择notesDom下所有子内容
		range.collapseToEnd(); // 光标移至最后
	});
};

export const formatSize = (size: number): string => {
	const units = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
	const _size = size ? Math.log(size) : 0;
	const index = Math.floor(_size / Math.log(1024));
	const newSize = size / Math.pow(1024, index);
	const strSize = index > 1 ? newSize.toFixed(2) : Math.floor(newSize);
	return `${strSize} ${units[index]}`;
};

// get file suffix type
export const getFileSuffix = (url: string) => {
	if (!url) return null;
	const filename = url.substring(url.lastIndexOf("/") + 1);
	return filename.substring(filename.lastIndexOf(".") + 1);
};
/**
 * 处理分割数据 对数据赋值默认值
 * @param timestamp
 */
export const timeStampPB2Date = (timestamp: any) => {
	if (!timestamp || !timestamp.seconds || !timestamp.seconds.mul) {
		// 这块需要约定一个 出错的 默认值, 或者魔法值
		return timestamp;
	}
	try {
		return new Date(
			timestamp.seconds
				.mul(1e3)
				.add(timestamp.nanos / 1e6)
				.toNumber()
		);
	} catch {
		return new Date();
	}
};
/**
 * 判断是否long类型
 * @param {any} obj
 */
export function longValue(obj: any): any {
	try {
		return obj.toString();
	} catch (e) {
		return obj;
	}
}
/**
 * 去除对象空值
 * paramRemove
 * @param data
 */
export const paramRemove = (data: any) => {
	Object.keys(data).forEach((item) => {
		if (isEmpty(data[item])) {
			delete data[item];
		}
	});
	return data;
};
/**
 * 去除对象空值
 * @param obj
 * @param
 * isEmpty
 * paramRemove
 */
export const isEmpty = (obj: any) => {
	return typeof obj === "undefined" || obj === null || obj === "";
};
/**
 * 拼接 baseUrl 和 path 形成完整的 URL
 * 示例正则表达式，用于验证 URL 格式
 * @param baseUrl
 */
// export const isValidUrl = (baseUrl: string) => {
// 	// ".jpg", ".jpeg", ".png", ".webp", ".gif", ".bmp", ".jfif", ".heic", ".tiff": // 图片
// 	// ".mp4", ".avi", ".mkv", ".mov", ".wmv", ".flv", ".webm": // 视频
// 	// ".docx", ".doc", ".pdf", ".xls", ".xlsx", ".csv": // 文件
// 	const regexPattern =
// 		/^(https?|ftp):\/\/.*\.(m3u8||pdf|js|jpg|heic|jpeg|png|gif|jfif|tiff|bmp|webp|mp4|mov|wmv|avi|mkv|flv|webm|docx|doc|pdf|xls|xlsx|csv)$/i;
// 	const regex = new RegExp(regexPattern);
// 	return regex.test(baseUrl);
// };

export const isValidUrl = (url: string) => {
	const regex =
	  /^(https?|ftp):\/\/.+\.(m3u8|pdf|js|jpg|heic|jpeg|png|gif|jfif|tiff|bmp|webp|mp4|mov|wmv|avi|mkv|flv|webm|docx|doc|xls|xlsx|csv)(\?.*)?$/i;
	return regex.test(url);
  };
export const acceptLimitsType = {
	image: ["image/png", "image/jpeg", "image/bmp", "image/gif", "image/x-ms-bmp"],
	video: ["video/mp4", "video/x-msvideo", "video/webm", "video/vnd.sealedmedia.softseal.mov", "video/quicktime", "video/x-flv", "video/x-ms-wmv"],
	doc: [
		"application/msword",
		"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
		"application/pdf",
		"application/vnd.ms-excel",
		"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		"text/csv",
	],
};

/**
 * 将对象转换为 URLSearchParams
 * @param obj - 要转换的对象
 * @param prefix - 可选的键前缀，用于处理嵌套对象
 * @returns URLSearchParams
 */
export function toUrlSearchParams(obj: Record<string, any>, prefix: string = ""): URLSearchParams {
	const params = new URLSearchParams();

	Object.keys(obj).forEach((key) => {
		const value = obj[key];
		const paramKey = prefix ? `${prefix}[${key}]` : key;

		if (Array.isArray(value)) {
			value.forEach((item) => params.append(paramKey, item));
		} else if (typeof value === "object" && value !== null) {
			const nestedParams = toUrlSearchParams(value, paramKey);
			nestedParams.forEach((v, k) => params.append(k, v));
		} else if (value !== undefined && value !== null) {
			params.append(paramKey, value);
		}
	});

	return params;
}

/**
 * 获取特定url参数key 的值
 * @param key string
 * @returns string
 */
export const getUrlParamsVal = (key?: string): string | Record<string, string> => {
	const params = new URLSearchParams(window.location.search);
	if (key) {
		return params.get(key) ?? "";
	}
	const result: Record<string, string> = {};
	params.forEach((value, paramKey) => {
		result[paramKey] = value;
	});
	return result;
};

const isNil = (value: any) => {
	return value == null; // 同时处理 null 和 undefined
};

export const isFlag = (obj: any) => {
	if (typeof obj !== "object") return false;
	for (const i in obj) {
		if (isNil(obj[i])) return false;
	}
	return true;
};

export const parseObj = (params: any) => {
	const decodedStr = decodeURIComponent(params);
	// 替换单引号为双引号
	const jsonStr = decodedStr.replace(/‘/g, '"').replace(/’/g, '"').replace(/'/g, '"');
	// 解析为对象
	const obj = JSON.parse(jsonStr);
	return obj;
};

/**
 * 判读是不是NG棋牌
 */
export const isNGqp = () => {
	try {
		const params = getUrlParamsVal("custom"); // 解码 URL 编码
		if (params) {
			const obj = parseObj(params);
			// console.log("ddd", obj, isNil(obj?.chatId));
			// console.log("toUrlSearchParams", toUrlSearchParams(obj).get("id"));
			if (isFlag(obj) && obj?.hash === HashOnly.merchant1) {
				return {
					...obj,
				};
			}
		}
	} catch (e) {
		return false;
	}
	return false;
};
export const isShowBackIcon = () => {
	const params = getUrlParamsVal("back"); // 解码 URL 编码
	if (params) {
		return params;
	}
	return false;
};

// eslint-disable-next-line security/detect-unsafe-regex
const tagRegex = /<\/?[a-zA-Z][\w-]*(\s+[a-zA-Z_:][\w:.-]*(\s*=\s*(?:"[^"]*"|'[^']*'|[^'">\s]+))?)*\s*\/?>/i;

export function isValidHtmlTag(tag: string) {
	return tagRegex.test(tag);
}

// 自定义错误上报方法
export function startReportError(_errorData: { code: number; payload: string }) {
	// 发送到后端日志服务
	// errorReportUpload({ data: [{ ...errorData, url: window.location.href, platform: "Platform_H5", createdAt: new Date().toISOString() }] })
	// 	.then(() => {
	// 		console.log("上报成功");
	// 	})
	// 	.catch((err) => {
	// 		console.error(" 上报时出错了: ", err);
	// 	});
}

//获取文件名字
export const getFileNameAndIcon = (url?: string, type?: string) => {
	if (url) {
		try {
			const sptArr = url.split("/");
			const name = sptArr[sptArr.length - 1];
			const fileFormat = ["pdf", "pptx", "excel", "world", "csv", "xlsx", "xls", "xlsm", "doc", "docx", "dotx"];
			const prefix = name.split(".")[0];
			const suffix = name.split(".")[1];
			let fileSvg = "excel";
			if (type === "file") {
				const isFile = fileFormat.some((v) => name.includes(v));
				if (isFile) {
					switch (suffix) {
						case "excel":
							fileSvg = "excel";
							break;
						case "pdf":
							fileSvg = "pdf";
							break;
						case "pptx":
							fileSvg = "pdf";
							break;
						case "doc":
							fileSvg = "world";
							break;
						case "docx":
							fileSvg = "world";
							break;
						case "dotx":
							fileSvg = "world";
							break;
						case "xls":
							fileSvg = "excel";
							break;
						case "xlsx":
							fileSvg = "excel";
							break;
						case "xlsm":
							fileSvg = "excel";
							break;
						default:
							fileSvg = "unkown";
					}
					return {
						name: prefix,
						suffix,
						fileSvg,
					};
				}
				return {
					name: "[文件]",
					suffix: "xlsx",
					fileSvg,
				};
			}
			const videoFormat = ["mp4", "MP4", "webm", "x-msvideo", "x-matroska", "x-ms-wmv", "x-flv", "mp3"];
			const isvideo = videoFormat.some((v) => name.includes(v));
			if (isvideo) {
				return {
					name,
					suffix,
				};
			}
			return {
				name: "[视频]",
				suffix: "mp4",
			};
		} catch (e) {
			if (type === "file") {
				return {
					name: "[文件]",
					suffix: "xlsx",
					fileSvg: "excel",
				};
			}
			return {
				name: "[视频]",
				suffix: "mp4",
			};
		}
	}
};

export const handleLinkClick = (event: any) => {
	if (!event.target) return;
	try {
		const aTarget = event.target.closest("a");
		if (aTarget?.href) {
			if (!/^https?:\/\//i.test(aTarget.href)) return;
			event.preventDefault();
			event.stopPropagation();
			if (typeof (window as any).openBrowser === "function") (window as any).openBrowser(aTarget.href);
			else window.open(aTarget.href, "_blank", "noopener,noreferrer");
		}
		const imgTarget = event.target.closest("img");
		if (imgTarget?.src) {
			event.preventDefault();
			event.stopPropagation();
            showImagePreview({ images: [useImgUrl(imgTarget.src)], closeable: true })
		}
	} catch (e) {
		showToast(typeof e === "string" ? e : JSON.stringify(e));
	}
};
